import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { $advisorClientPage } from 'models/advisor-client-dashboard-model';
import { $queryParams } from 'models/location';
import { $global } from 'models/store-global';

import './AdvisorClientMenu.scss';

import { Frame, Link } from 'components/StyledTemplates/StyledTemplates';
import { MenuBlock, MenuBlockHeader } from 'components/pages/Dashboard/common/CommunityPageStyles';

import useResources from 'hooks/useResources';

import calendar_icon from 'assets/icons/calendar_today_24px.svg';
import imgIconsDashboardSidebarCommunity from 'assets/icons/dashboard-sidebar-community.svg';
import imgIconsDashboardSidebarEstatePlan from 'assets/icons/dashboard-sidebar-estate-plan.svg';
import mail_icon from 'assets/icons/mail.svg';
import user_icon from 'assets/icons/perm_identity_24px_outlined.svg';

import { type Participant } from 'types/zod/participant';

const AdvisorClientMenu = () => {
    const { t } = useResources(['common', 'assessment']);

    const { selectedClientId, submenu } = useUnit($advisorClientPage);
    const { back } = useUnit($queryParams);

    const community = useUnit(
        $global.map((state) => state.advisorClient?.clients?.[selectedClientId || -1]?.community),
    );
    const participants = community?.realm || [];
    const qInstances = useUnit(
        $global.map((state) => state.advisorClient?.clients?.[selectedClientId || -1]?.qInstances),
    );
    const hasTrustFund = !!Object.values(qInstances || {}).find(
        (qInstance) => qInstance.questionnaire_type_code === 'TRUST_FUND',
    );
    const hasGiftTrust = !!Object.values(qInstances || {}).find(
        (qInstance) => qInstance.questionnaire_type_code === 'GIFT_TRUST',
    );

    const menu = [
        [
            {
                type: `header`,
                label: t(`advisor.clientDashboard.menu.ep`),
                icon: imgIconsDashboardSidebarEstatePlan,
                link: `/advisor/client/${selectedClientId}/ep`,
                visible: true,
            },
            {
                type: `header`,
                label: t(`advisor.clientDashboard.menu.trustFunding`),
                icon: imgIconsDashboardSidebarCommunity,
                link: `/advisor/client/${selectedClientId}/trustFunding`,
                visible: hasTrustFund,
            },
            {
                type: `header`,
                label: t(`advisor.clientDashboard.menu.giftTrust`),
                icon: imgIconsDashboardSidebarCommunity,
                link: `/advisor/client/${selectedClientId}/giftTrust`,
                visible: hasGiftTrust,
            },
            {
                type: `header`,
                label: t(`advisor.clientDashboard.menu.community`),
                icon: imgIconsDashboardSidebarCommunity,
                link: `/advisor/client/${selectedClientId}/community`,
                visible: true,
            },
            /*{
                type: `header`,
                label: t(`advisor.clientDashboard.menu.checklist`),
                icon: imgIconsDashboardSidebarRoles,
                link: `/advisor/client/${id}/checklist`,
                visible: true,
            },*/
        ],
    ];

    if (!participants.length) {
        return <></>;
    }

    return (
        <div className="advisor-client-menu">
            <div className="info-block">
                <div className="back-link">
                    <Link to={back || '/advisor/clients'}>{t('advisor.clientDashboard.sidebar.back')}</Link>
                </div>
                <div className="name serif d-flex flex-column">
                    {(community?.tabs || [])
                        .map((i: number) => {
                            const p = participants.find((j: Participant) => j.participant_id === i);
                            return `${p?.first_name} ${p?.last_name}`;
                        })
                        .map((i: string, index: number, self: string[]) => (
                            <span key={index}>{i + (index < self.length - 1 ? ' & ' : '')}</span>
                        ))}
                </div>
                {community?.plan && <div className="plan">{t(`assessment:price_line.${community.plan}`)}</div>}
                <div className="plan-info">
                    <div className="plan-info-row">
                        <img src={calendar_icon} alt="calendar" />
                        <span className="info-label">{t('advisor.clientDashboard.sidebar.created')}:</span>
                        {dayjs(participants[0].date_added).format(`MMMM DD, YYYY`)}
                    </div>
                    <div className="plan-info-row">
                        <img src={mail_icon} alt="mail" />
                        <span className="info-label">{t('advisor.clientDashboard.sidebar.contact')}:</span>
                        {t(participants[0].email)}
                    </div>
                    <div className="plan-info-row">
                        <img src={user_icon} alt="user" className="user-img" />
                        <span className="info-label">{t('advisor.clientDashboard.sidebar.participants')}:</span>
                        {participants.length - 1}
                    </div>
                </div>
            </div>

            {menu.map((block, index) => {
                return (
                    <MenuBlock key={index}>
                        {block.map((item: any, index: number) => {
                            if (!item.visible) {
                                return null;
                            }
                            switch (item.type) {
                                case 'header':
                                    return (
                                        <MenuBlockHeader
                                            key={index}
                                            icon={item?.icon}
                                            as={item?.link ? Link : Frame}
                                            to={item?.link}
                                            selectable={true}
                                            selected={item?.link && item.link.split('/').pop() === submenu}
                                            extra="border-left: 1px solid #322c3e; border-right: 1px solid #322c3e; border-bottom: 1px solid #322c3e;"
                                        >
                                            {item?.label}
                                        </MenuBlockHeader>
                                    );

                                default:
                                    return null;
                            }
                        })}
                    </MenuBlock>
                );
            })}
        </div>
    );
};

export default AdvisorClientMenu;
